import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Settings, CheckCircle, Clock, Star, AlertTriangle } from 'lucide-react';
import { useGoogleCalendar } from '../../hooks/useGoogleCalendar';
import { useSupporter } from '../../hooks/useSupporter';

const GoogleCalendarConnect = ({ onConnectionChange, onSettingsClick }) => {
  const { t } = useTranslation();
  const { isSupporter, loading: supporterLoading } = useSupporter();
  const { 
    connected, 
    loading: calendarLoading, 
    error, 
    calendarsConfigured,
    calendarsEnabled,
    startGoogleAuth, 
    disconnectCalendar,
    configured = true // Default to true for backward compatibility
  } = useGoogleCalendar();
  
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [showConfirmDisconnect, setShowConfirmDisconnect] = useState(false);

  // Track when both hooks have finished loading
  React.useEffect(() => {
    if (!supporterLoading && !calendarLoading) {
      setHasInitialized(true);
    }
  }, [supporterLoading, calendarLoading]);

  const handleConnect = async () => {
    try {
      setIsConnecting(true);
      await startGoogleAuth();
      if (onConnectionChange) {
        onConnectionChange(true);
      }
    } catch (error) {
      console.error('Failed to connect Google Calendar:', error);
      // Error handling could be improved with toast notifications
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectClick = () => {
    setShowConfirmDisconnect(true);
  };

  const handleDisconnect = async () => {
    try {
      setIsDisconnecting(true);
      await disconnectCalendar();
      if (onConnectionChange) {
        onConnectionChange(false);
      }
      setShowConfirmDisconnect(false);
    } catch (error) {
      console.error('Failed to disconnect Google Calendar:', error);
      
      // For certain error types, still trigger connection change
      // as the local cleanup might have succeeded even if Google revocation failed
      if (error.message && (
        error.message.includes('Invalid or expired token') ||
        error.message.includes('Failed to revoke Google Calendar access, but local settings have been cleared')
      )) {
        console.log('Google token revocation failed, but triggering local state refresh...');
        
        // Still trigger connection change as local cleanup likely succeeded
        if (onConnectionChange) {
          onConnectionChange(false);
        }
      }
      
      setShowConfirmDisconnect(false);
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleCancelDisconnect = () => {
    setShowConfirmDisconnect(false);
  };

  // Show loading until both hooks have initialized
  if (!hasInitialized || supporterLoading || calendarLoading) {
    return (
      <div className="google-calendar-connect">
        <div className="calendar-status loading">
          <div className="status-icon"><Clock size={16} /></div>
          <div className="status-content">
            <p>{t('calendar.loadingStatus')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!configured) {
    return (
      <div className="google-calendar-connect">
        <div className="calendar-status not-configured">
          <div className="status-icon"><Settings size={16} /></div>
          <div className="status-content">
            <h4>{t('calendar.integrationTitle')}</h4>
            <p>{t('calendar.notConfigured')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isSupporter) {
    return (
      <div className="google-calendar-connect">
        <div className="calendar-status supporter-required">
          <div className="status-icon"><Calendar size={16} /></div>
          <div className="status-content">
            <h4>{t('calendar.integrationTitle')}</h4>
            <p>{t('upgrade.calendarBodyShort')}</p>
            <button className="upgrade-button" disabled>
              <Star size={16} className="inline mr-1" /> {t('upgrade.upgradeToSupporter')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (connected) {
    return (
      <button
        onClick={onSettingsClick}
        className="user-profile-dropdown-item calendar-settings-link"
      >
        <span className="calendar-settings-icon"><CheckCircle size={16} /></span>
        {t('auth.googleCalendarSettings')}
      </button>
    );
  }

  return (
    <div className="google-calendar-connect">
      <div className="calendar-status disconnected">
        <div className="status-icon"><Calendar size={16} /></div>
        <div className="status-content">
          <h4>{t('calendar.connectTitle')}</h4>
          <p>{t('calendar.connectBody')}</p>
          {error && (
            <div className="error-message">
              <span><AlertTriangle size={16} className="inline mr-1" /> {error.includes('authorization expired') || error.includes('Please reconnect') 
                ? t('calendar.expired')
                : error}
              </span>
            </div>
          )}
          
          {/* Disconnect Confirmation - only show if needed */}
          {showConfirmDisconnect && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              padding: '16px',
              margin: '16px 0',
              color: '#991b1b'
            }}>
              <p style={{ margin: '0 0 12px 0', fontWeight: '600' }}>
                {t('calendar.confirmDisconnect')}
              </p>
              <p style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#7f1d1d' }}>
                {t('calendar.confirmDisconnectBody')}
              </p>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={handleDisconnect}
                  disabled={isDisconnecting}
                  style={{
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer',
                    fontWeight: '500'
                  }}
                >
                  {isDisconnecting ? t('calendar.disconnecting') : t('calendar.confirmDisconnectYes')}
                </button>
                <button
                  onClick={handleCancelDisconnect}
                  disabled={isDisconnecting}
                  style={{
                    backgroundColor: 'white',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    padding: '8px 16px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    cursor: 'pointer'
                  }}
                >
                  {t('buttons.cancel')}
                </button>
              </div>
            </div>
          )}
          
          <button 
            className="connect-button"
            onClick={handleConnect}
            disabled={isConnecting}
          >
            {isConnecting ? t('calendar.connecting') : t('calendar.connectButton')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarConnect;